import { cn } from '@/lib/utils'
import type { CorrectionData } from '@/hooks/useVisualTools'

interface CorrectionWidgetProps {
  data: CorrectionData
  onClose: () => void
  className?: string
}

export function CorrectionWidget({ data, onClose, className }: CorrectionWidgetProps) {
  return (
    <div
      className={cn(
        'rounded-xl border-2 p-5 shadow-lg animate-fade-in',
        'bg-gradient-to-br from-rose-50 to-emerald-50',
        'border-border',
        className
      )}
    >
      {/* Header with close button */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-semibold text-muted-foreground">
          Correction
        </span>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-full hover:bg-black/5"
          aria-label="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>

      {/* Incorrect text with strikethrough */}
      <div className="flex items-start gap-3 mb-3">
        <span className="shrink-0 w-6 h-6 rounded-full bg-rose-100 flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-rose-600"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </span>
        <p className="text-lg text-rose-700 line-through decoration-rose-400 decoration-2">
          {data.incorrect}
        </p>
      </div>

      {/* Correct text */}
      <div className="flex items-start gap-3">
        <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-emerald-600"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </span>
        <p className="text-lg text-emerald-700 font-semibold">
          {data.correct}
        </p>
      </div>

      {/* Explanation */}
      {data.explanation && (
        <div className="mt-4 pt-3 border-t border-border/50">
          <p className="text-sm text-muted-foreground">
            {data.explanation}
          </p>
        </div>
      )}
    </div>
  )
}
